import { useEffect, useState } from 'react'
import { usePageVisibility } from 'react-page-visibility'
import { compact, isEqual } from 'lodash'
import { useImperativeQuery } from 'utils/hooks/useImperativeQuery'
import { IVendorsQuery, VendorsDocument } from 'apps/marketplace/graphql/Vendors'

export interface IShipperFacilityVendorData {
  vendorId: string
  vendorName: string
  vendorFacilityAddress: string
  vendorShipperFacilityAddress: string
}

export interface IUseShipperFacilityVendorsState {
  loaded: boolean
  shipperFacilityVendorData: IShipperFacilityVendorData[]
}

export interface IUseShipperFacilityProps {
  facilityUuid: string
  shipperFacilityUuid: string | undefined
}

const useShipperFacilityVendors = ({
  facilityUuid,
  shipperFacilityUuid,
}: IUseShipperFacilityProps): IUseShipperFacilityVendorsState => {
  const [loaded, setLoaded] = useState<boolean>(false)
  const [vendors, setVendors] = useState<IShipperFacilityVendorData[]>([])
  const isPageVisible = usePageVisibility()
  const fetchVendors = useImperativeQuery(VendorsDocument)

  const updateVendors = async () => {
    if (!facilityUuid || !shipperFacilityUuid) {
      return
    }
    const { data } = await fetchVendors({ shipperFacilityUuid, facilityUuid })
    const { vendors }: IVendorsQuery = data || {}
    const { nodes } = vendors || {}
    const vendorNodes = compact(nodes) || []
    const newVendors = vendorNodes.map(vendor => {
      // Get vendor facility address, there should only be one record in vendorFacilities array since we are filtering by facilityUuid
      const vendorFacilityAddress = vendor.vendorFacilities?.[0]?.metadata?.addressLine2 || ''
      // Get vendor shipper facility address, there should only be one record in vendorShipperFacilities array since we are filtering by shipperFacilityUuid
      const vendorShipperFacilityAddress =
        vendor.vendorShipperFacilities?.[0]?.metadata?.addressLine2 || ''

      return {
        vendorId: vendor.id,
        vendorName: vendor.name,
        vendorFacilityAddress,
        vendorShipperFacilityAddress,
      }
    })
    if (!isEqual(newVendors, vendors)) {
      setVendors(newVendors)
    }
    setLoaded(true)
  }

  // Hook for getting vendors associated with current facility, refetched if page visibility changes
  useEffect(() => {
    if (isPageVisible && facilityUuid && shipperFacilityUuid) {
      updateVendors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityUuid, isPageVisible, shipperFacilityUuid])

  return {
    loaded,
    shipperFacilityVendorData: vendors,
  }
}

export default useShipperFacilityVendors
